/**
 * Admin — Health/Pipeline (Wave 1b W1b-20)
 *
 * Backed by the real /admin/health/pipeline + history endpoints. Reindex
 * and retry actions are shown as unavailable until workers are wired.
 *
 * Surfaces:
 *   - 7-stage pipeline tile grid (docling -> qdrant_writer)
 *   - Bottleneck banner (lowest-throughput stage)
 *   - Per-stage history line chart (Recharts)
 *   - Reindex unavailable state
 *   - Per-stage retry unavailable states
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Workflow, ArrowRight, AlertTriangle, CheckCircle2, XCircle,
  Activity, Clock, TrendingUp, RefreshCw, RotateCcw,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend,
} from 'recharts';
import { CHART_TOOLTIP } from '@/lib/chart-theme';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { SectionCard } from '@/components/admin';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/lib/format';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/admin/health/pipeline')({
  component: PipelinePage,
});

// ─── API types ───────────────────────────────────────────────────────────

const STAGE_NAMES = [
  'docling',
  'chunker',
  'entity_extractor',
  'contextual_enricher',
  'embedder',
  'keyword_indexer',
  'qdrant_writer',
] as const;
type StageName = (typeof STAGE_NAMES)[number];

const STAGE_DESCRIPTIONS: Record<StageName, string> = {
  docling: 'IBM Docling parser (Qwen-VL fallback)',
  chunker: 'Recursive + safety-atomic chunking',
  entity_extractor: 'Equipment / part / chemical NER',
  contextual_enricher: 'Anthropic contextual enrichment (LLM)',
  embedder: 'nomic-embed-text v1.5',
  keyword_indexer: 'Keyword text index update',
  qdrant_writer: 'Insert into tenant Qdrant collection',
};

interface StageMetric {
  stageName: string;
  throughputPerHour: number;
  queueDepth: number;
  latencyP50Ms: number;
  latencyP95Ms: number;
  errorCount24h: number;
  lastRunAt: string | null;
  observedAt: string | null;
}

interface PipelineSnapshot {
  stages: StageMetric[];
  bottleneckStage: string | null;
  overallThroughputPerHour: number;
  collectedAt: string;
}

interface HistoryPoint {
  observedAt: string;
  throughputPerHour: number;
  queueDepth: number;
  latencyP50Ms: number;
  latencyP95Ms: number;
  errorCount24h: number;
}

interface HistoryResponse {
  stageName: string;
  history: HistoryPoint[];
}

// ─── Page ────────────────────────────────────────────────────────────────

function PipelinePage() {
  const [chartStage, setChartStage] = useState<StageName>('contextual_enricher');

  const snapshotQuery = useQuery<PipelineSnapshot>({
    queryKey: ['admin', 'health', 'pipeline'],
    queryFn: () => apiClient.get<PipelineSnapshot>('/admin/health/pipeline'),
    refetchInterval: 30_000,
  });

  const historyQuery = useQuery<HistoryResponse>({
    queryKey: ['admin', 'health', 'pipeline', chartStage, 'history'],
    queryFn: () =>
      apiClient.get<HistoryResponse>(
        `/admin/health/pipeline/${encodeURIComponent(chartStage)}/history?limit=24`,
      ),
  });

  const stages = snapshotQuery.data?.stages ?? [];
  const bottleneck = snapshotQuery.data?.bottleneckStage ?? null;
  const overallThroughput = snapshotQuery.data?.overallThroughputPerHour ?? 0;

  const reportedStages = stages.filter((s) => s.throughputPerHour > 0);
  const slowestThroughput =
    reportedStages.length > 0
      ? Math.min(...reportedStages.map((s) => s.throughputPerHour))
      : 0;
  const totalQueue = stages.reduce((sum, s) => sum + s.queueDepth, 0);
  const errorStages = stages.filter((s) => s.errorCount24h > 0).length;

  // Honest empty-state: the API returns canonical 7-stage zero placeholder
  // rows when no `pipeline_stage_metrics` rows exist. Distinguish "telemetry
  // worker hasn't run yet" from "real zero throughput".
  const hasTelemetry = stages.some(
    (s) =>
      s.throughputPerHour > 0 ||
      s.queueDepth > 0 ||
      s.latencyP95Ms > 0 ||
      s.errorCount24h > 0 ||
      s.lastRunAt !== null,
  );

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="health-pipeline">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Ingestion Pipeline</h1>
          <p className="text-xs text-muted-foreground mt-1">
            7-stage RAG ingestion pipeline. Document upload → embedding → indexing.
            Bottlenecks show as queue depth.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1.5"
          disabled
          title="Full reindex is not wired in V1; no worker is available to execute it."
          data-testid="reindex-button"
        >
          <RefreshCw className="h-3 w-3" />
          Reindex unavailable
        </Button>
      </div>

      {/* No-telemetry banner — distinguish "metrics worker hasn't run yet" from "real zero traffic" */}
      {!snapshotQuery.isLoading && !snapshotQuery.isError && !hasTelemetry && (
        <div className="border-l-4 border-l-blue-500 bg-blue-500/5 p-3 rounded text-xs flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Pipeline telemetry not yet recorded.</span>{' '}
            The metrics-emitter worker (W1b-08, scheduled for Wave 2) hasn&apos;t
            written any <code className="font-mono">pipeline_stage_metrics</code> rows
            for this tenant yet. Stage tiles below show canonical zero
            placeholders rather than live measurements until the worker runs.
            Document ingestion still functions normally; only the per-stage
            observability is missing.
          </div>
        </div>
      )}

      {/* Bottleneck banner */}
      {bottleneck && (
        <div
          className="border-l-4 border-l-amber-500 bg-amber-500/5 p-3 rounded text-xs flex items-start gap-2"
          data-testid="bottleneck-banner"
        >
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Bottleneck:</span>{' '}
            <span className="font-mono">{bottleneck}</span>{' '}
            is the slowest stage at {slowestThroughput}/hr. The end-to-end
            pipeline throughput is rate-limited by this stage.
          </div>
        </div>
      )}

      {/* Loading / error */}
      {snapshotQuery.isLoading && (
        <div className="text-xs text-muted-foreground">Loading pipeline metrics…</div>
      )}
      {snapshotQuery.isError && (
        <div className="border-l-4 border-l-red-500 bg-red-500/5 p-3 rounded text-xs flex items-start gap-2">
          <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <div>
            Failed to load pipeline metrics.
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs ml-2"
              onClick={() => snapshotQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Total queue depth"
          value={totalQueue.toString()}
          accent={totalQueue > 100 ? 'red' : totalQueue > 50 ? 'amber' : 'emerald'}
          icon={Activity}
        />
        <StatCard
          label="Slowest stage"
          value={bottleneck ?? '—'}
          subValue={
            bottleneck
              ? `${slowestThroughput}/hr throughput`
              : 'No data yet'
          }
          accent="slate"
          icon={Clock}
        />
        <StatCard
          label="Stages with errors (24h)"
          value={errorStages.toString()}
          accent={errorStages > 0 ? 'amber' : 'emerald'}
          icon={errorStages > 0 ? AlertTriangle : CheckCircle2}
        />
        <StatCard
          label="Pipeline throughput"
          value={`${overallThroughput}/h`}
          subValue="Sum across all 7 stages"
          accent="slate"
          icon={TrendingUp}
        />
      </div>

      {/* Pipeline diagram (compact horizontal) */}
      <SectionCard
        title="Flow Diagram"
        icon={Workflow}
        description="Linear flow from raw document to indexed embeddings."
      >
        <div className="flex items-stretch gap-1 overflow-x-auto pb-2" data-testid="flow-diagram">
          {stages.map((stage, idx) => {
            const status =
              stage.errorCount24h > 0
                ? 'failed'
                : stage.queueDepth > 10
                  ? 'degraded'
                  : 'healthy';
            const tone =
              status === 'healthy'
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : status === 'degraded'
                  ? 'border-amber-500/40 bg-amber-500/5'
                  : 'border-red-500/40 bg-red-500/5';
            return (
              <div key={stage.stageName} className="flex items-center gap-1 shrink-0">
                <div className={cn('border rounded-md p-2 min-w-[130px]', tone)}>
                  <div className="text-[10px] font-medium truncate">
                    {stage.stageName}
                  </div>
                  <div className="text-[9px] text-muted-foreground tabular-nums">
                    {stage.latencyP95Ms}ms p95 · {stage.queueDepth} queued
                  </div>
                </div>
                {idx < stages.length - 1 && (
                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Pipeline detailed cards */}
      <SectionCard
        title="Stages"
        icon={Workflow}
        description="Per-stage metrics. Retry controls are unavailable in V1; no dead-letter drain is wired."
      >
        <div className="space-y-2">
          {stages.map((stage, idx) => (
            <StageCard
              key={stage.stageName}
              stage={stage}
              index={idx}
              isBottleneck={bottleneck === stage.stageName}
              retryDisabled
            />
          ))}
        </div>
      </SectionCard>

      {/* History chart */}
      <SectionCard
        title="History"
        icon={TrendingUp}
        description="Last 24 observations for the selected stage."
      >
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="text-xs text-muted-foreground">Stage</span>
          <Select
            value={chartStage}
            onValueChange={(v) => setChartStage(v as StageName)}
          >
            <SelectTrigger className="w-56 h-8 text-xs" data-testid="history-stage-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_NAMES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="h-64">
          {historyQuery.isLoading && (
            <div className="text-xs text-muted-foreground py-4">
              Loading history…
            </div>
          )}
          {historyQuery.data && historyQuery.data.history.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={historyQuery.data.history}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border/40"
                  vertical={false}
                />
                <XAxis
                  dataKey="observedAt"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) =>
                    new Date(v).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  }
                  className="text-muted-foreground"
                />
                <YAxis
                  yAxisId="throughput"
                  tick={{ fontSize: 10 }}
                  className="text-muted-foreground"
                  width={40}
                />
                <YAxis
                  yAxisId="latency"
                  orientation="right"
                  tick={{ fontSize: 10 }}
                  className="text-muted-foreground"
                  width={40}
                />
                <Tooltip
                  {...CHART_TOOLTIP}
                  labelFormatter={(v) => new Date(v as string).toLocaleString()}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Line
                  yAxisId="throughput"
                  type="monotone"
                  dataKey="throughputPerHour"
                  name="Throughput/h"
                  stroke="rgb(34 197 94)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="latency"
                  type="monotone"
                  dataKey="latencyP95Ms"
                  name="p95 latency (ms)"
                  stroke="rgb(99 102 241)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="throughput"
                  type="monotone"
                  dataKey="queueDepth"
                  name="Queue depth"
                  stroke="rgb(245 158 11)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            !historyQuery.isLoading && (
              <div className="text-xs text-muted-foreground py-4">
                No history available.
              </div>
            )
          )}
        </div>
      </SectionCard>

    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function StatCard({
  label, value, subValue, accent, icon: Icon,
}: {
  label: string;
  value: string;
  subValue?: string;
  accent: 'emerald' | 'amber' | 'red' | 'slate';
  icon: React.ComponentType<{ className?: string }>;
}) {
  const border =
    accent === 'emerald'
      ? 'border-l-emerald-500'
      : accent === 'amber'
        ? 'border-l-amber-500'
        : accent === 'red'
          ? 'border-l-red-500'
          : 'border-l-slate-400';
  const iconColor =
    accent === 'emerald'
      ? 'text-emerald-500'
      : accent === 'amber'
        ? 'text-amber-500'
        : accent === 'red'
          ? 'text-red-500'
          : 'text-slate-500';
  return (
    <Card className={cn('border-l-4', border)}>
      <CardContent className="p-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
            {label}
          </div>
          <div className="text-base font-semibold tabular-nums mt-0.5 truncate">
            {value}
          </div>
          {subValue && (
            <div className="text-[10px] text-muted-foreground">{subValue}</div>
          )}
        </div>
        <Icon className={cn('h-4 w-4 shrink-0', iconColor)} />
      </CardContent>
    </Card>
  );
}

function StageCard({
  stage, index, isBottleneck, retryDisabled,
}: {
  stage: StageMetric;
  index: number;
  isBottleneck: boolean;
  retryDisabled: boolean;
}) {
  const status =
    stage.errorCount24h > 0
      ? 'failed'
      : stage.queueDepth > 10
        ? 'degraded'
        : 'healthy';
  const Icon =
    status === 'healthy'
      ? CheckCircle2
      : status === 'degraded'
        ? AlertTriangle
        : XCircle;
  const tone =
    status === 'healthy'
      ? 'border-emerald-500/30 bg-emerald-500/5'
      : status === 'degraded'
        ? 'border-amber-500/30 bg-amber-500/5'
        : 'border-red-500/30 bg-red-500/5';
  const iconColor =
    status === 'healthy'
      ? 'text-emerald-500'
      : status === 'degraded'
        ? 'text-amber-500'
        : 'text-red-500';
  const badgeBg =
    status === 'healthy'
      ? 'bg-emerald-500/15'
      : status === 'degraded'
        ? 'bg-amber-500/15'
        : 'bg-red-500/15';

  const isStageKnown = (STAGE_NAMES as readonly string[]).includes(stage.stageName);
  const description = isStageKnown
    ? STAGE_DESCRIPTIONS[stage.stageName as StageName]
    : '';

  return (
    <div
      className={cn('border rounded-md p-3', tone)}
      data-testid={`stage-card-${stage.stageName}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'h-7 w-7 rounded flex items-center justify-center shrink-0',
            badgeBg,
          )}
        >
          <span className="text-[10px] font-bold tabular-nums">{index + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium">{stage.stageName}</span>
            <Icon className={cn('h-3 w-3', iconColor)} />
            {isBottleneck && (
              <Badge
                variant="outline"
                className="h-4 text-[9px] px-1.5 border-amber-500 text-amber-600 dark:text-amber-400"
              >
                BOTTLENECK
              </Badge>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {description}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
            <Metric
              label="Throughput"
              value={`${stage.throughputPerHour}/h`}
            />
            <Metric label="Queue" value={stage.queueDepth.toString()} alert={stage.queueDepth > 10} />
            <Metric label="p50" value={`${stage.latencyP50Ms}ms`} />
            <Metric
              label="p95"
              value={`${stage.latencyP95Ms}ms`}
              alert={stage.latencyP95Ms > 3000}
            />
          </div>
          <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
            {stage.lastRunAt && (
              <span className="flex items-center gap-1">
                <Clock className="h-2.5 w-2.5" />
                Last run {relativeTime(stage.lastRunAt)}
              </span>
            )}
            <span>{stage.errorCount24h} errors (24h)</span>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px] gap-1 ml-auto"
              disabled={retryDisabled}
              title={`Retry for ${stage.stageName} is not wired in V1.`}
              data-testid={`retry-button-${stage.stageName}`}
            >
              <RotateCcw className="h-2.5 w-2.5" />
              Retry unavailable
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div>
      <div className="text-[9px] text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div
        className={cn(
          'font-mono tabular-nums text-[11px]',
          alert && 'text-red-600 dark:text-red-400 font-semibold',
        )}
      >
        {value}
      </div>
    </div>
  );
}
